import { useMemo } from "react";
import {
  buildShiftLookupMaps,
  getDefaultShiftsSortedBySequence,
  getShiftColumnGroupKey,
  getShiftMainTemplateCode,
  normalizeScheduleCellCode,
  resolveShiftFromScheduleCellFromMaps,
  shouldPreferShiftDefinition,
} from "@/utils/shift-util";
import {
  sortDisplayShiftsUsedOnlyOrder,
  type ShiftItem,
  type ShiftWithBand,
} from "./useSortShift";

type NurseLike = {
  schedule?: readonly (string | null | { readonly code: string | null })[] | null;
  schedule_ids?: readonly (number | null)[] | null;
};

/**
 * 근무표 헤더 통계 칩과 행별 ShiftStatus 열을 동일하게 맞춘다.
 * - 열은 **실제 근무표에 등장한 DB `id`만** (동일 shift_id 두 행이어도 쓴 쪽만 컬럼 생성)
 * - 정렬: default_shift 메인 템플릿 행 우선 → shift_gb 띠 → `sequence` → `id`
 * - `use_mid === false`이면 M(default_shift) 행은 컬럼에서 제외(기존과 동일)
 * - `includeBaselineMainTemplateColumns`: 메인 템플릿(D/E/N 등, 오프 제외)은 스케줄에 한 번도 없어도 열에 포함
 *   (원티드 관리보드 등 스냅샷이 비어 있어도 헤더·행 통계 열이 메인 근무와 맞게 유지)
 */
export function useRosterDisplayShiftColumns<
  T extends ShiftItem & { id?: number },
>(
  copyNurses: readonly NurseLike[] | null | undefined,
  shiftsFull: T[] | null | undefined,
  isUseMid: boolean | undefined,
  options?: { includeBaselineMainTemplateColumns?: boolean }
) {
  return useMemo(() => {
    const shifts = shiftsFull ?? [];
    if (shifts.length === 0) {
      return { displayShifts: [] as T[], columnShiftIds: [] as string[] };
    }

    const lookupMaps = buildShiftLookupMaps(shifts);

    const rosterUsedIds = new Set<number>();
    (copyNurses ?? []).forEach((n) => {
      const sched = n.schedule ?? [];
      sched.forEach((code, i) => {
        const trimmed = normalizeScheduleCellCode(code).trim();
        if (!trimmed || trimmed === "-") return;
        const resolved = resolveShiftFromScheduleCellFromMaps(
          trimmed,
          n.schedule_ids?.[i] ?? null,
          lookupMaps
        );
        if (resolved?.id != null && Number.isFinite(Number(resolved.id))) {
          rosterUsedIds.add(Number(resolved.id));
        }
      });
    });

    const columnIds = new Set<number>(rosterUsedIds);
    if (options?.includeBaselineMainTemplateColumns) {
      getDefaultShiftsSortedBySequence(
        shifts as Parameters<typeof getDefaultShiftsSortedBySequence>[0],
        { excludeOff: false }
      ).forEach((s) => {
        const row = s as T;
        if (isUseMid === false && getShiftMainTemplateCode(row) === "M") return;
        if (row.id != null && Number.isFinite(Number(row.id))) {
          columnIds.add(Number(row.id));
        }
      });
    }

    const usedRows = shifts.filter((s) => {
      if (s.id == null || !Number.isFinite(Number(s.id))) return false;
      if (!columnIds.has(Number(s.id))) return false;
      if (isUseMid === false && getShiftMainTemplateCode(s) === "M") return false;
      return true;
    });

    const canonicalMainRows = new Map<string, T>();
    getDefaultShiftsSortedBySequence(
      shifts as Parameters<typeof getDefaultShiftsSortedBySequence>[0],
      { excludeOff: false }
    ).forEach((row) => {
      const shift = row as T;
      if (isUseMid === false && getShiftMainTemplateCode(shift) === "M") return;
      const key = getShiftColumnGroupKey(shift);
      const current = canonicalMainRows.get(key);
      if (shouldPreferShiftDefinition(current, shift)) {
        canonicalMainRows.set(key, shift);
      }
    });

    const collapsedRows = new Map<string, T>();
    sortDisplayShiftsUsedOnlyOrder(usedRows as ShiftWithBand[]).forEach(
      (row) => {
        const shift = row as T;
        const key = getShiftColumnGroupKey(shift);
        if (!collapsedRows.has(key)) {
          collapsedRows.set(key, canonicalMainRows.get(key) ?? shift);
        }
      }
    );

    const displayShifts = sortDisplayShiftsUsedOnlyOrder(
      [...collapsedRows.values()] as ShiftWithBand[]
    ) as T[];

    return {
      displayShifts,
      columnShiftIds: displayShifts.map((s) => s.shift_id),
    };
  }, [
    copyNurses,
    shiftsFull,
    isUseMid,
    options?.includeBaselineMainTemplateColumns,
  ]);
}
